// The scope an issue screen (the board or the table) renders. A single
// workflow groups by stage and enables stage drag/reorder + kanban
// subscriptions; a team, member, workflow set, or fixed issue set spans many
// workflows, so those features are off and the board groups by workflow
// instead. Both screens take an IssueScope prop and branch on
// `scopeWorkflowId(scope) !== undefined` — the one switch for "stage features
// available". Pure (no React) so it can be reused anywhere and unit-tested.

export type IssueScope =
  | { kind: "workflow"; workflowId: string }
  // A workspace dashboard's source set — one or more workflows, editable on
  // the dashboard itself. Distinct from `workflow` because nothing stage-
  // specific (grouping, drag, bulk stage moves) applies across several.
  | { kind: "workflows"; workflowIds: string[] }
  // `includeShared` widens the team's own projects (its Teams property) with
  // the ones shared directly with it — the team page's list and board, on
  // the view's "Show shared Projects" switch (on by default), where a Teams
  // column tells the two apart. Dashboards leave it off: their counts are
  // the work the team is assigned.
  | { kind: "team"; teamId: string; includeShared?: boolean }
  | { kind: "member"; memberId: string }
  // A fixed set of linked issues (e.g. an account's or contact's projects).
  | { kind: "issues"; issueIds: string[] }
  // The projects other people handed the viewer — an enabled item share for
  // them or their team, never their own creations. Viewer-relative: the server
  // resolves it against the caller, so the scope carries no id.
  | { kind: "shared" };

/** The mandatory predicate that scopes every issue query for this screen. */
export function scopeWhere(scope: IssueScope): Record<string, unknown> {
  switch (scope.kind) {
    case "workflow":
      return { workflowId: { eq: scope.workflowId } };
    case "workflows":
      // The server ignores an empty `in: []` (the where would degenerate to
      // "all issues"); `exists: false` on the never-null column is the
      // explicit empty set — a dashboard with no source yet shows nothing.
      return scope.workflowIds.length
        ? { workflowId: { in: scope.workflowIds } }
        : { workflowId: { exists: false } };
    // The work assigned to the team — the project's own Teams property —
    // plus, with `includeShared`, projects shared with it. Never the members'
    // other assignments: that double-counts a project across every team its
    // assignees sit on and shifts when rosters change.
    case "team":
      return scope.includeShared
        ? {
            OR: [
              { teamIds: { hasSome: [scope.teamId] } },
              { sharedWithTeamIds: { hasSome: [scope.teamId] } },
            ],
          }
        : { teamIds: { hasSome: [scope.teamId] } };
    case "member":
      return { assignees: { entityId: { hasSome: [scope.memberId] } } };
    case "issues":
      // The server ignores an empty `in: []` (the where would degenerate to
      // "all issues"); `exists: false` on the never-null id column is the
      // explicit empty set.
      return scope.issueIds.length
        ? { issueId: { in: scope.issueIds } }
        : { issueId: { exists: false } };
    case "shared":
      return { sharedWithViewer: true };
  }
}

/**
 * The workflow id when the scope is a single workflow, else undefined. Its
 * presence gates everything workflow-specific: stage grouping/drag,
 * kanban subscriptions, the stage-co-fetching list query, and bulk change-stage.
 */
export function scopeWorkflowId(scope: IssueScope): string | undefined {
  return scope.kind === "workflow" ? scope.workflowId : undefined;
}

/**
 * The workflow ids the scope's rows can carry — one for a single workflow, the
 * set for a workflow set, undefined when the scope does not name workflows at
 * all (a team's or member's projects span every workflow). This is what a
 * stage picker narrows on: a stage of a workflow the scope excludes can match
 * nothing the screen shows.
 */
export function scopeWorkflowIds(scope: IssueScope): string[] | undefined {
  switch (scope.kind) {
    case "workflow":
      return [scope.workflowId];
    case "workflows":
      return scope.workflowIds;
    default:
      return undefined;
  }
}

/**
 * Whether the scope's rows all carry a workflow the scope named — a single
 * workflow or a workflow set. The activity widgets fold the entity predicate
 * into their issue-side filter only under such a scope: a team or member
 * predicate names assignees, which the activity feed cannot apply to issues.
 * Takes the kind as a plain string because the published dashboard scope
 * carries it untyped.
 */
export function scopeIsWorkflowBound(kind: string): boolean {
  return kind === "workflow" || kind === "workflows";
}

/** Default board grouping: stage for a single workflow, workflow across many. */
export function defaultBoardGrouping(scope: IssueScope): "stage" | "workflow" {
  return scope.kind === "workflow" ? "stage" : "workflow";
}

/** Stable string identity for React keys that must reset per scope. */
export function scopeKey(scope: IssueScope): string {
  switch (scope.kind) {
    case "workflow":
      return `workflow:${scope.workflowId}`;
    case "workflows":
      return `workflows:${scope.workflowIds.join(",")}`;
    case "team":
      return `team:${scope.teamId}`;
    case "member":
      return `member:${scope.memberId}`;
    case "issues":
      return `issues:${scope.issueIds.join(",")}`;
    case "shared":
      return "shared";
  }
}

/**
 * The scope a published dashboard entity (`{type, id}`) names — the inverse of
 * `scopeEntityId`, so a widget rendered under a dashboard can recover the
 * screen's scope from the context it already reads. An unknown type (or none)
 * is no scope.
 */
export function scopeFromEntity(
  entity: { type: string; id: string } | null | undefined,
): IssueScope | undefined {
  if (!entity) return undefined;
  const ids = entity.id ? entity.id.split(",") : [];
  switch (entity.type) {
    case "workflow":
      return { kind: "workflow", workflowId: entity.id };
    case "workflows":
      return { kind: "workflows", workflowIds: ids };
    case "team":
      return { kind: "team", teamId: entity.id };
    case "member":
      return { kind: "member", memberId: entity.id };
    case "issues":
      return { kind: "issues", issueIds: ids };
    case "shared":
      return { kind: "shared" };
    default:
      return undefined;
  }
}

/** The bare entity id the scope targets, regardless of kind. */
export function scopeEntityId(scope: IssueScope): string {
  switch (scope.kind) {
    case "workflow":
      return scope.workflowId;
    case "workflows":
      return scope.workflowIds.join(",");
    case "team":
      return scope.teamId;
    case "member":
      return scope.memberId;
    case "issues":
      return scope.issueIds.join(",");
    case "shared":
      // Viewer-relative: the viewer IS the entity, and the server knows who
      // that is. The empty id keeps `{type, id}` round-trippable.
      return "";
  }
}
